import { BlogPost } from '@/types/blog';

interface PostsListProps {
  posts: BlogPost[];
  onEdit: (post: BlogPost) => void;
  onDelete: (post: BlogPost) => void;
  onTogglePublish: (post: BlogPost) => void;
  loading: boolean;
}

export default function PostsList({ posts, onEdit, onDelete, onTogglePublish, loading }: PostsListProps) {
  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="flex items-center gap-3 text-[#0A1829]/40">
          <i className="ri-loader-4-line animate-spin text-xl"></i>
          <span className="font-sans text-sm">Carregando posts...</span>
        </div>
      </div>
    );
  }

  if (posts.length === 0) {
    return (
      <div className="text-center py-20">
        <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4 rounded-full bg-[#F8F5EE] border border-[#E9D59C]/50">
          <i className="ri-article-line text-[#B39B62] text-2xl"></i>
        </div>
        <h3 className="font-serif text-xl text-[#0A1829] font-light mb-2">Nenhum post ainda</h3>
        <p className="font-sans text-[#0A1829]/50 text-sm">Clique em &ldquo;Novo post&rdquo; para criar o primeiro artigo.</p>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead>
          <tr className="border-b border-[#E9D59C]/50">
            <th className="text-left font-sans text-xs font-semibold tracking-wider uppercase text-[#0A1829]/40 pb-3 pr-4">
              Título
            </th>
            <th className="text-left font-sans text-xs font-semibold tracking-wider uppercase text-[#0A1829]/40 pb-3 pr-4 hidden md:table-cell">
              Categoria
            </th>
            <th className="text-left font-sans text-xs font-semibold tracking-wider uppercase text-[#0A1829]/40 pb-3 pr-4 hidden lg:table-cell">
              Data
            </th>
            <th className="text-center font-sans text-xs font-semibold tracking-wider uppercase text-[#0A1829]/40 pb-3 pr-4">
              Status
            </th>
            <th className="text-right font-sans text-xs font-semibold tracking-wider uppercase text-[#0A1829]/40 pb-3">
              Ações
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-[#E9D59C]/30">
          {posts.map((post) => (
            <tr key={post.id} className="hover:bg-[#F8F5EE]/50 transition-colors">
              <td className="py-4 pr-4">
                <div className="flex items-center gap-3">
                  {post.imagem_url ? (
                    <div className="w-10 h-10 rounded overflow-hidden shrink-0 border border-[#E9D59C]/30">
                      <img
                        src={post.imagem_url}
                        alt={post.titulo}
                        className="w-full h-full object-cover object-top"
                      />
                    </div>
                  ) : (
                    <div className="w-10 h-10 rounded bg-[#F8F5EE] border border-[#E9D59C]/30 flex items-center justify-center shrink-0">
                      <i className="ri-image-line text-[#B39B62]/50 text-sm"></i>
                    </div>
                  )}
                  <div>
                    <p className="font-serif text-sm text-[#0A1829] font-medium leading-snug line-clamp-2 max-w-xs">
                      {post.titulo}
                    </p>
                    <p className="font-sans text-xs text-[#0A1829]/40 mt-0.5">{post.tempo_leitura}</p>
                  </div>
                </div>
              </td>
              <td className="py-4 pr-4 hidden md:table-cell">
                <span className="font-sans text-xs text-[#B39B62] font-semibold tracking-wider uppercase bg-[#B39B62]/10 border border-[#B39B62]/20 px-2 py-0.5 rounded-full whitespace-nowrap">
                  {post.categoria}
                </span>
              </td>
              <td className="py-4 pr-4 hidden lg:table-cell">
                <span className="font-sans text-xs text-[#0A1829]/50">
                  {new Date(post.data_publicacao).toLocaleDateString('pt-BR', {
                    day: '2-digit',
                    month: 'short',
                    year: 'numeric',
                  })}
                </span>
              </td>
              <td className="py-4 pr-4 text-center">
                <button
                  onClick={() => onTogglePublish(post)}
                  className={`inline-flex items-center gap-1.5 font-sans text-xs font-semibold px-3 py-1 rounded-full cursor-pointer transition-all duration-200 whitespace-nowrap ${
                    post.publicado
                      ? 'bg-green-50 text-green-600 border border-green-200 hover:bg-green-100'
                      : 'bg-[#0A1829]/5 text-[#0A1829]/50 border border-[#0A1829]/10 hover:bg-[#0A1829]/10'
                  }`}
                >
                  <i className={`${post.publicado ? 'ri-eye-line' : 'ri-eye-off-line'} text-xs`}></i>
                  {post.publicado ? 'Publicado' : 'Rascunho'}
                </button>
              </td>
              <td className="py-4 text-right">
                <div className="flex items-center justify-end gap-2">
                  <button
                    onClick={() => onEdit(post)}
                    className="w-8 h-8 flex items-center justify-center rounded-md bg-[#F8F5EE] border border-[#E9D59C]/50 text-[#0A1829]/60 hover:text-[#B39B62] hover:border-[#B39B62]/30 transition-all cursor-pointer"
                    title="Editar"
                  >
                    <i className="ri-edit-line text-sm"></i>
                  </button>
                  <button
                    onClick={() => onDelete(post)}
                    className="w-8 h-8 flex items-center justify-center rounded-md bg-red-50 border border-red-100 text-red-400 hover:text-red-600 hover:border-red-200 transition-all cursor-pointer"
                    title="Excluir"
                  >
                    <i className="ri-delete-bin-line text-sm"></i>
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
